/**
 * The clock behind the countdown — **one** `setInterval` for the whole app
 * (PLAN §3), not a timer per component.
 *
 * Why it matters: the bar can show two legs, each with a ring, a pace meter
 * and a ticking clock. Give each of those its own timer and you get half a
 * dozen unsynchronized intervals whose seconds visibly disagree, all keeping
 * the tab awake. Instead the app root starts a single interval that writes
 * `now` here, and everything that needs the time subscribes to this store.
 *
 * It's a module-level snapshot behind `useSyncExternalStore` — the same shape
 * as the filter store, and for the same reason: the value is one number, the
 * snapshot is referentially stable, and non-React callers can read it
 * directly. Nothing is persisted; `now` is the wall clock, not user state.
 *
 * Two details that are easy to get wrong:
 *  - **Ref-counted start.** `StrictMode` mounts effects twice in development,
 *    and the sidebar/sheet swap can remount the shell. Starting the clock is
 *    idempotent; the interval is cleared only when the last holder lets go.
 *  - **Re-sync on wake.** Background tabs throttle `setInterval` to once a
 *    minute or worse, so a tab returning from the background can be a minute
 *    stale. `visibilitychange` snaps it back before the next paint.
 */

import { useEffect, useSyncExternalStore } from "react";

/** One tick a second: the finest granularity the clock display shows. */
const TICK_MS = 1000;

const listeners = new Set<() => void>();
let now = Date.now();
let handle: ReturnType<typeof setInterval> | null = null;
let holders = 0;

function setNow(next: number): void {
  if (next === now) return;
  now = next;
  for (const listener of listeners) listener();
}

const tick = (): void => setNow(Date.now());

const onVisibilityChange = (): void => {
  if (document.visibilityState === "visible") tick();
};

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

const getSnapshot = (): number => now;

/**
 * Start the app-wide clock and get back the stop function. Safe to call more
 * than once; the interval exists while at least one caller holds it.
 */
export function startCountdownClock(): () => void {
  holders += 1;
  if (handle === null) {
    tick();
    handle = setInterval(tick, TICK_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders === 0 && handle !== null) {
      clearInterval(handle);
      handle = null;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    }
  };
}

/**
 * Run the clock for as long as this component is mounted. Called **once**, by
 * the app root — that's the whole point of this module.
 */
export function useCountdownClock(): void {
  useEffect(() => startCountdownClock(), []);
}

/** Subscribe to the clock. Only components that show time should call this. */
export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Read the clock outside React. */
export function readNow(): number {
  return now;
}

/** Test seam: pin the clock to a fixed instant. */
export function setNowForTests(instant: number): void {
  setNow(instant);
}
